// @flow
import moment from 'moment';
import _ from 'lodash';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import ViewUtil from '../ViewUtil';
import {
  Annotation,
  AnnotationThread,
  FuidToUnreadComments,
  CollectionUnreadCountEntry,
  ZmCollection,
  FuidRrnInfo
} from './../autogenerated/ZoomBridge';
import Store from '../store/Store';
import UnreadCommentsData from '../queries/annotations/UnreadCommentsData';
import Authenticator from '../protocol/Authenticator';
import { SELECTED_COLLECTION, FULL_TIME_FORMAT } from '../store/Constants';
import CollectionSettingsUtil from '../queries/lightbox/userRoles/CollectionSettingsUtil';
import ThumbnailUtil from './ThumbnailUtil';
import HashMap from '../protocol/HashMap';
import t from '../i18n/I18n';
import { bool } from 'prop-types';
import HashSet from '../protocol/HashSet';

const debug = require('../debug/debug')('wc:util:CommentUtil');

export default class CommentUtil {
  static drawEditedOrModified(comment: AnnotationThread | Annotation) {
    let cTime, mTime;

    if (comment instanceof AnnotationThread) {
      cTime = AnnotationUtil.getRootAnnotation(comment).getCTime();
      mTime = AnnotationUtil.getRootAnnotation(comment).getMTime();
    } else if (comment instanceof Annotation) {
      cTime = comment.getCTime();
      mTime = comment.getMTime();
    }
    if (!mTime || Math.abs(cTime - mTime) < 1000) {
      return t('CREATED') + ' ' + this.getTimeFormat(cTime);
    } else {
      return t('MODIFIED') + ' ' + this.getTimeFormat(mTime);
    }
  }

  static getTimeFormat(time) {
    const fullTimeFormat = Store.get(FULL_TIME_FORMAT);
    if (fullTimeFormat) {
      return moment(time).format('MMM DD YYYY hh:mm:ss A');
    } else {
      return moment(time).fromNow();
    }
  }

  static toggleTimeFormat() {
    Store.put(FULL_TIME_FORMAT, !Store.get(FULL_TIME_FORMAT));
  }

  static commentBackgroundByKind(comment: AnnotationThread) {
    switch (comment.getAnnotationKind()) {
      case AnnotationUtil.COMMENT:
        return 'var(--primary-style-color)';
      //case AnnotationUtil.PRIVATE_COMMENT: return '#FFA500';
      case AnnotationUtil.NOTE:
        return '#FFA500';
      case AnnotationUtil.PRIVATE_NOTE:
        return '#FFA500';
      default:
        return 'var(--primary-style-color)';
    }
  }

  static getRootCommentText(comment: AnnotationThread) {
    return AnnotationUtil.getRootAnnotation(comment).getText();
  }

  static findNearestLeft(arr, num) {
    if (arr.length === 0 || num === 0) return 0;
    const midIdx = parseInt(arr.length / 2);
    const midEl = arr[midIdx];
    if (midEl === num && midIdx !== 0) return midIdx - 1;
    if (num < midEl) {
      for (let i = midIdx; i >= 0; i -= 1) {
        if (arr[i] < num) return i;
      }
    } else {
      for (let i = arr.length; i >= midIdx; i -= 1) {
        if (arr[i] < num) return i;
      }
    }
    return 0;
  }

  static findNearestRight(arr, num) {
    if (arr.length === 0) return 0;
    const midIdx = parseInt(arr.length / 2);
    const midEl = arr[midIdx];
    if (num < midEl) {
      for (let i = 0; i <= midIdx; i += 1) {
        if (arr[i] > num) return i;
      }
    } else {
      for (let i = midIdx; i < arr.length; i += 1) {
        if (arr[i] > num) return i;
      }
    }
    return arr.length;
  }

  static isInRange(targetPos, currPos, threshold) {
    return !(currPos - targetPos >= threshold || threshold <= targetPos - currPos);
  }

  static maxCommentIndex(comments: AnnotationThread[]) {
    const result = _.maxBy(comments, (comment) => {
      if (comment && comment._annotationLocation && comment._annotationLocation._spatialLocation) {
        return comment._annotationLocation._commentIndex;
      } else return 0;
    });
    return result ? result._annotationLocation._commentIndex : 0;
  }

  static getUnresolvedComments(asset, collectionId, onResult) {
    AnnotationUtil.getAnnotations(AnnotationUtil.repoItemToFuidRrnInfo(asset), collectionId, 0, (getAnnotations) => {
      let comments = getAnnotations.getAnnotations();
      comments = _.remove(comments, (c) => {
        return !c.isResolved();
      });
      onResult(comments);
    });
  }

  static parseCommentData(comment: AnnotationThread) {
    let data = {
      text: CommentUtil.getRootCommentText(comment),
      resolved: comment.isResolved(),
      replies: comment
        .getAnnotation()
        .getReplies()
        .map((r: Annotation) => r.getText())
    };

    const location = AnnotationUtil.getSpatialLocation(comment);
    if (location) {
      data.x = location.getWidthOffset();
      data.y = location.getHeightOffset();
    }

    const time = AnnotationUtil.getVideOffsetInMilliseconds(comment);
    if (time) {
      data.time = time;
    }

    return data;
  }

  static compareComments(lhs, rhs) {
    const a = CommentUtil.parseCommentData(lhs);
    const b = CommentUtil.parseCommentData(rhs);
    return _.isEqual(a, b);
  }

  static filterUniqueComments(from, to) {
    _.remove(from, (fromEl) => {
      const found = _.find(to, (toEl) => {
        return CommentUtil.compareComments(fromEl, toEl);
      });

      if (found) {
        debug('[filterUniqueComments()] found a match for comment: ', from);
      } else {
        debug('[filterUniqueComments()] no match for comment: ', from);
      }

      return found;
    });
  }

  static copyComments(fromAsset, toAsset, collectionId) {
    //$FlowFixMe
    return new Promise((resolve, reject) => {
      debug('[copyComments()] fetching unresolved comments for asset: ', fromAsset);
      CommentUtil.getUnresolvedComments(fromAsset, collectionId, (comments) => {
        debug(`[copyComments()] fetched ${comments.length} assets`);
        const fuidRrnInfo = AnnotationUtil.repoItemToFuidRrnInfo(toAsset);
        CommentUtil.saveCommentsToAsset(comments, fuidRrnInfo, collectionId, true)
          .then(resolve)
          .catch(reject);
      });
    });
  }

  static saveCommentsToAsset(comments, fuidRrnInfo, collectionId, copyComments = false) {
    //$FlowFixMe
    return new Promise((resolve, reject) => {
      if (comments.length === 0) {
        debug('[saveCommentsToAsset()] 0 comments. done.');
        return resolve();
      }

      debug('[saveCommentsToAsset()] fetching all comments for asset: ', fuidRrnInfo);
      AnnotationUtil.getAnnotations(fuidRrnInfo, collectionId, 0, (getAnnotations) => {
        let targetAssetComments = getAnnotations.getAnnotations() || [];
        debug(`[saveCommentsToAsset()] fetched ${targetAssetComments.length} comments`);
        let maxTargetAssetCommentId = CommentUtil.maxCommentIndex(targetAssetComments);
        CommentUtil.filterUniqueComments(comments, targetAssetComments);
        debug(`[saveCommentsToAsset()] ${comments.length} comments after filtering`);
        const sortedComments = _.sortBy(comments, (c) => c._annotationLocation._commentIndex);
        const promises = sortedComments.map((comment) => {
          maxTargetAssetCommentId += 1;
          CommentUtil.resetIds(comment);
          comment.getAnnotationLocation()._commentIndex = maxTargetAssetCommentId;
          debug('[saveCommentsToAsset()] saving new comment: ', comment);
          return AnnotationUtil.saveAnnotation(fuidRrnInfo, comment, collectionId, null, copyComments);
        });

        Promise.all(promises)
          .then(resolve)
          .catch(reject);
      });
    });
  }

  /**
   * Reset the ids of annotation threads. Currently goes upto one level. Not deep reset. Only shallow right now.
   * @param {AnnotationThread} comment
   */
  static resetIds(comment: AnnotationThread) {
    comment.setThreadId(0);
    /**
     * @type {Annotation}
     */
    const annotation = comment.getAnnotation();
    annotation.setCommentId(0);
    _.forEach(annotation.getReplies(), function(reply) {
      reply.setCommentId(0);
      reply.setParentCommentId(0);
    });

    _.forEach(annotation.getAttachments(), function(attachment) {
      attachment.setCommentId(0);
      attachment.setParentCommentId(0);
    });
  }

  static markCommentAsReadIfNeeded(thread: AnnotationThread, checkAllInThread: boolean = false) {
    debug('[markCommentAsReadIfNeeded()] args: ', { thread, checkAllInThread });
    if (thread) {
      const threadId = thread.getThreadId();
      const fuidToUnreadComments: FuidToUnreadComments = CommentUtil.getFuidToUnreadComments();
      debug('[markCommentAsReadIfNeeded()] fuidToUnreadComments: ', fuidToUnreadComments);
      if (!fuidToUnreadComments) {
        return;
      }

      if (!CommentUtil.isUnread(thread)) {
        debug('[markCommentAsReadIfNeeded()] !CommentUtil.isUnread(thread)');
        if (!checkAllInThread) {
          debug('[markCommentAsReadIfNeeded()] !checkAllInThread. return.');
          return;
        }

        const commentIdToThreadIds = fuidToUnreadComments
          .getCommentIdToThreadId()
          .toArray()
          .filter((o) => o.value === threadId);

        debug('[markCommentAsReadIfNeeded()] commentIdToThreadIds: ', commentIdToThreadIds);
        const unreadCommentIds = fuidToUnreadComments.getUnreadCommentsIds().toArray();
        debug('[markCommentAsReadIfNeeded()] unreadCommentIds: ', unreadCommentIds);
        const found = commentIdToThreadIds.filter((o) => _.includes(unreadCommentIds, parseInt(o.key, 10))).length > 0;
        if (!found) {
          debug('[markCommentAsReadIfNeeded()] unreadCommentIds not found in commentIdToThreadIds. return.');
          return;
        }
      }

      fuidToUnreadComments.getUnreadCommentsIds().clear();

      const collectionId = ViewUtil.getCollectionIdFromArgs();
      CommentUtil.markCommentAsRead(threadId, fuidToUnreadComments.getFuidRrnInfo(), collectionId);
    }
  }

  static markCommentAsRead(threadId: number, fuidRrnInfo: FuidRrnInfo, collectionId: number): void {
    AnnotationUtil.markCommentAsRead([threadId], [], collectionId, fuidRrnInfo)
      .then(() => {
        AnnotationUtil.loadAndRenderUnreadCommentDetailInWindowGlobalObject();
      })
      .catch((err) => {
        debug('[markCommentAsReadIfNeeded()] error: ', err);
      });
  }

  static markCommentIdsAsRead(commentIds: Array<number>, fuidRrnInfo: FuidRrnInfo, collectionId: number): void {
    AnnotationUtil.markCommentAsRead([], commentIds, collectionId, fuidRrnInfo)
      .then(() => {
        AnnotationUtil.loadAndRenderUnreadCommentDetailInWindowGlobalObject();
      })
      .catch((err) => {
        debug('[markCommentAsReadIfNeeded()] error: ', err);
      });
  }
  /**
   *
   * @param {AnnotationThread} thread
   * @param {Annotation} reply
   * @return {boolean} true if the root or reply commentId is unread
   */
  static isUnread(thread: AnnotationThread, reply: Annotation = null) {
    if (!thread) return false;
    const root = AnnotationUtil.getRootAnnotation(thread);
    if (!root) {
      return false;
    }

    const commentId = (reply || root).getCommentId();
    /**
     * @type FuidToUnreadComments
     */
    const fuidToUnreadComments = CommentUtil.getFuidToUnreadComments();
    if (!fuidToUnreadComments || !fuidToUnreadComments.getUnreadCommentsIds()) {
      return false;
    }

    return fuidToUnreadComments.getUnreadCommentsIds().contains(commentId);
  }

  /**
   *
   * @param {AnnotationThread} thread
   * @return {boolean}
   */
  static isAnyCommentUnreadInThread(thread: AnnotationThread): boolean {
    if (CommentUtil.isUnread(thread)) {
      return true;
    }

    const root = AnnotationUtil.getRootAnnotation(thread);
    if (!root) {
      return false;
    }

    const replies = root.getReplies();
    for (let i = 0; i < replies.length; i++) {
      if (CommentUtil.isUnread(thread, replies[i])) {
        return true;
      }
    }
    return false;
  }

  /**
   *
   * @param {AnnotationThread} thread
   * @return {boolean}
   */
  static getUnreadCommentIdsInTheseThreads(threads: Array<AnnotationThread>, collectionId: number): Array<number> {
    const unreadCommentIds: Array<number> = [];
    if (threads) {
      for (let index = 0; index < threads.length; index++) {
        const thread = threads[index];
        this.getUnreadCommentIdsInThisThread(thread, collectionId, unreadCommentIds);
      }
    }
    return unreadCommentIds;
  }

  /**
   *
   * @param {AnnotationThread} thread
   * @return {boolean}
   */
  static getUnreadCommentIdsInThisThread(
    thread: AnnotationThread,
    collectionId: number,
    storage: Array<number> = []
  ): Array<number> {
    const unreadCommentIds: Array<number> = storage ? storage : [];
    const allUnreadCommentIdsForThisCollection = this.getUnreadCommentIdsCollectionAndFuid(collectionId);

    if (thread) {
      const root: Annotation = AnnotationUtil.getRootAnnotation(thread);
      if (root) {
        if (_.includes(allUnreadCommentIdsForThisCollection, root.getCommentId())) {
          unreadCommentIds.push(root.getCommentId());
        }
      }

      const replies = root.getReplies();
      if (replies) {
        for (let i = 0; i < replies.length; i++) {
          const replyCommentId = replies[i].getCommentId();
          if (_.includes(allUnreadCommentIdsForThisCollection, replyCommentId)) {
            unreadCommentIds.push(replyCommentId);
          }
        }
      }
    }
    return unreadCommentIds;
  }

  static forEachUnreadCommentIdsFromGlobalMap(
    onEachUnReadCommentId: (unreadCommentId: number, fuidRrnInfo: FuidRrnInfo, collectionId: number) => void,
    fuidFilter: (fuidRrnInfo: FuidRrnInfo) => boolean = (item) => true,
    collectionIdFilter: (collectionId: number) => boolean = (collectionId) => true
  ) {
    const unreadCommentsData: ?UnreadCommentsData = Store.get(UnreadCommentsData.NAME);
    const collectionUnreadCountEntries = unreadCommentsData
      ? unreadCommentsData.getCollectionUnreadCountEntries()
      : null;

    if (collectionUnreadCountEntries) {
      for (
        let collectionUnreadIndex = 0;
        collectionUnreadIndex < collectionUnreadCountEntries.length;
        collectionUnreadIndex++
      ) {
        const collectionUnreadCountEntry: CollectionUnreadCountEntry =
          collectionUnreadCountEntries[collectionUnreadIndex];
        const collectionId = collectionUnreadCountEntry.getCollectionId();
        if (!collectionIdFilter(collectionId)) continue;
        const fuidToUnreadComments = collectionUnreadCountEntry.getFuidUnreadComments();
        if (fuidToUnreadComments) {
          for (let fuidUnreadIndex = 0; fuidUnreadIndex < fuidToUnreadComments.length; fuidUnreadIndex++) {
            const fuidToUnreadComment: FuidToUnreadComments = fuidToUnreadComments[fuidUnreadIndex];
            const fuidRrnInfo: FuidRrnInfo = fuidToUnreadComment.getFuidRrnInfo();
            if (!fuidFilter(fuidRrnInfo)) continue;
            const unreadCommentIds: ?Array<number> = fuidToUnreadComment.getUnreadCommentsIds()
              ? fuidToUnreadComment.getUnreadCommentsIds().toArray()
              : null;

            if (unreadCommentIds) {
              for (
                let unreadCommentIdIndex = 0;
                unreadCommentIdIndex < unreadCommentIds.length;
                unreadCommentIdIndex++
              ) {
                const unreadCommentId: number = unreadCommentIds[unreadCommentIdIndex];
                onEachUnReadCommentId(unreadCommentId, fuidRrnInfo, collectionId);
              }
            }
          }
        }
      }
    }
  }

  static getUnreadCommentIdsCollectionAndFuid(
    collectionId: number,
    fuidFilter: (fuidRrnInfo: FuidRrnInfo) => boolean = (item) => true,
    storage: Array<number> = []
  ): Array<number> {
    const unreadCommentIdsToReturn: Array<number> = storage ? storage : [];

    const onEachUnReadCommentId = (unreadCommentId: number, fuidRrnInfo: FuidRrnInfo, collectionId: number) => {
      unreadCommentIdsToReturn.push(unreadCommentId);
    };
    this.forEachUnreadCommentIdsFromGlobalMap(
      onEachUnReadCommentId,
      fuidFilter,
      (toMatchCollectionId: number) => !collectionId || toMatchCollectionId == collectionId
    );

    return unreadCommentIdsToReturn;
  }

  /**
   * Please don't use. Use the variant of this method which takes collection id and fuid as method parameter.
   *
   * @deprecated
   */
  static getFuidToUnreadComments(): FuidToUnreadComments {
    const collectionId = ViewUtil.getCollectionIdFromArgs();
    const fuid = ViewUtil.getFuid();

    const unreadCommentsData: ?UnreadCommentsData = Store.get(UnreadCommentsData.NAME);
    // debug('[getFuidToUnreadComments()] unreadCommentsData: ', unreadCommentsData);
    const collectionUnreadCountEntries = unreadCommentsData ? unreadCommentsData.getCollectionUnreadCountEntries() : [];
    const entry: CollectionUnreadCountEntry = collectionUnreadCountEntries.find(
      (e: CollectionUnreadCountEntry) => e.getCollectionId() === collectionId
    );
    // debug('[getFuidToUnreadComments()] entry: ', entry);
    if (!entry || !entry.getFuidUnreadComments()) {
      return null;
    }
    const fuidToUnreadComments: FuidToUnreadComments = entry
      .getFuidUnreadComments()
      .find((fuidToUnreadComments) => fuidToUnreadComments.getFuidRrnInfo().getFuid() === fuid);

    // debug('[getFuidToUnreadComments()] fuidToUnreadComments: ', fuidToUnreadComments);
    if (!fuidToUnreadComments) {
      return null;
    }

    return fuidToUnreadComments;
  }

  static sortByRecentlyModified(comments: Array<AnnotationThread>) {
    //$FlowFixMe
    comments = _.sortBy(comments, (comment: AnnotationThread) => {
      const rootAnnotation = AnnotationUtil.getRootAnnotation(comment);
      return rootAnnotation.getMTime() > rootAnnotation.getCTime()
        ? rootAnnotation.getMTime()
        : rootAnnotation.getCTime();
    });
    _.reverse(comments);
    return comments;
  }

  static sortByDateAdded(comments) {
    //$FlowFixMe
    return _.sortBy(comments, (comment) => {
      const rootAnnotation = AnnotationUtil.getRootAnnotation(comment);
      return rootAnnotation.getCTime();
    });
  }

  static sortByAuthor(comments) {
    //$FlowFixMe
    return _.sortBy(comments, (comment) => {
      const rootAnnotation = AnnotationUtil.getRootAnnotation(comment);
      return rootAnnotation.getCreator();
    });
  }

  static sortByVideoTime(comments) {
    //$FlowFixMe
    return _.sortBy(comments, (comment) => {
      return AnnotationUtil.getVideOffsetInMilliseconds(comment);
    });
  }

  static isOwnerOrAdmin(comment: AnnotationThread) {
    if (!comment) {
      return false;
    }

    if (CommentUtil.isCreator(comment)) {
      return true;
    }

    const collection: ZmCollection = Store.get(SELECTED_COLLECTION);
    if (!collection) {
      return false;
    }

    return CollectionSettingsUtil.isOwnerOrSuperAdminOrAdmin(collection);
  }

  static isCreator(comment: AnnotationThread): boolean {
    return AnnotationUtil.isIamCreatorOfThisThread(comment);
  }

  static isReplyCreator(reply: Annotation): boolean {
    return AnnotationUtil.isIamCreatorOfThisAnnotation(reply);
  }

  /**
   * @type {HashMap}
   * @private
   */
  static userIdToEmailMap = new HashMap();

  //get email address from cache otherwise get from network call
  static async getEmailFromCache(creator: string): string {
    let email: string = CommentUtil.userIdToEmailMap.get(creator);
    if (email === undefined) {
      email = await ThumbnailUtil.fetchEmailAddress(creator);
      CommentUtil.userIdToEmailMap.put(creator, email);
    }
    return email;
  }

  /**
   * @type {number}
   * @private
   */
  static activeCommentThread: number = null;

  //Get
  static getActiveCommentThreadTime(): number {
    return CommentUtil.activeCommentThread;
  }

  static setActiveCommentThreadTime(comment: Annotation): void {
    if (comment) {
      CommentUtil.activeCommentThread = AnnotationUtil.getVideOffsetInMilliseconds(comment);
    } else {
      CommentUtil.activeCommentThread = null;
    }
  }
}
